import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiRefreshCw,
  FiPackage,
  FiAlertTriangle,
  FiActivity,
  FiTruck,
  FiFileText,
  FiCheckCircle,
  FiDownload,
  FiEye
} from 'react-icons/fi';

const TABS = {
  STOCK: 'stock',
  STOCK_IN: 'stock_in',
  HISTORY: 'history',
  PO: 'po',
  ANALYTICS: 'analytics'
};

function toSafeDecimalDifference(parLevel, currentStock, precision = 3) {
  const par = Number(parLevel || 0);
  const current = Number(currentStock || 0);
  const factor = 10 ** precision;
  const scaledPar = Math.round(par * factor);
  const scaledCurrent = Math.round(current * factor);
  const scaledDiff = scaledPar - scaledCurrent;
  return scaledDiff / factor;
}

function formatDecimalInput(value, precision = 3) {
  return Number(value || 0)
    .toFixed(precision)
    .replace(/\.0+$/, '')
    .replace(/(\.\d*?)0+$/, '$1');
}

function formatISTDateTime(value) {
  if (!value) return '-';

  let date;
  if (typeof value === 'string') {
    const hasTimezone = value.includes('Z') || value.includes('+') || /-\d{2}:\d{2}$/.test(value);
    date = new Date(hasTimezone ? value : `${value}Z`);
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
}

function InventoryManagement() {
  const { alert, confirm } = useModal();

  const [activeTab, setActiveTab] = useState(TABS.STOCK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [stockInReceipts, setStockInReceipts] = useState([]);
  const [editedStockHistory, setEditedStockHistory] = useState([]);
  const [alerts, setAlerts] = useState({ alertCount: 0, lowStockItems: [] });
  const [analytics, setAnalytics] = useState({
    topConsumedItems: [],
    wastageTrend: [],
    projectedDaysLeft: []
  });

  const [suppliers, setSuppliers] = useState([]);
  const [purchaseRequests, setPurchaseRequests] = useState([]);

  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showReceiptDetailsModal, setShowReceiptDetailsModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [stockInvoiceFile, setStockInvoiceFile] = useState(null);
  const [showPoPreviewModal, setShowPoPreviewModal] = useState(false);
  const [previewRequest, setPreviewRequest] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState('');

  const [itemForm, setItemForm] = useState({
    name: '',
    unit: 'unit',
    currentStock: '',
    parLevel: '',
    notes: ''
  });

  const [receiveForm, setReceiveForm] = useState({
    inventoryItemId: '',
    supplierId: '',
    quantity: '',
    receivedAt: '',
    invoiceNumber: '',
    invoiceNote: '',
    note: ''
  });

  const [supplierForm, setSupplierForm] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
    suppliedItemIds: []
  });

  const [requestForm, setRequestForm] = useState({
    inventoryItemId: '',
    supplierId: '',
    requestedQty: '',
    expectedDate: '',
    note: ''
  });

  const lowStockCount = alerts.alertCount || 0;
  const consumptionMovements = useMemo(
    () => (movements || []).filter((row) =>
      String(row.movement_type || '').toLowerCase() === 'consumption' &&
      !String(row.notes || '').startsWith('Manual stock edit by')
    ),
    [movements]
  );

  const supplierOptionsForRequestItem = useMemo(() => {
    if (!requestForm.inventoryItemId) return [];
    return suppliers.filter((supplier) => (supplier.supplied_item_ids || []).includes(requestForm.inventoryItemId));
  }, [suppliers, requestForm.inventoryItemId]);

  const supplierOptionsForReceiveItem = useMemo(() => {
    if (!receiveForm.inventoryItemId) return [];
    return suppliers.filter((supplier) => (supplier.supplied_item_ids || []).includes(receiveForm.inventoryItemId));
  }, [suppliers, receiveForm.inventoryItemId]);

  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      const [
        itemsResponse,
        movementResponse,
        receiptsResponse,
        editedStockResponse,
        alertResponse,
        analyticsResponse,
        supplierResponse,
        requestsResponse
      ] = await Promise.all([
        api.get('/api/inventory/items'),
        api.get('/api/inventory/movements?limit=80'),
        api.get('/api/inventory/receipts?limit=150'),
        api.get('/api/inventory/edited-stock?limit=200'),
        api.get('/api/inventory/alerts'),
        api.get('/api/inventory/analytics'),
        api.get('/api/inventory/suppliers'),
        api.get('/api/inventory/purchase-requests')
      ]);

      setItems(itemsResponse.data?.items || []);
      setMovements(movementResponse.data?.movements || []);
      setStockInReceipts(receiptsResponse.data?.receipts || []);
      setEditedStockHistory(editedStockResponse.data?.edits || []);
      setAlerts(alertResponse.data || { alertCount: 0, lowStockItems: [] });
      setAnalytics(analyticsResponse.data || { topConsumedItems: [], wastageTrend: [], projectedDaysLeft: [] });
      setSuppliers(supplierResponse.data?.suppliers || []);
      setPurchaseRequests(requestsResponse.data?.requests || []);
    } catch (error) {
      await alert('Failed to fetch inventory data', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryData();
  }, []);

  const resetItemForm = () => {
    setItemForm({
      name: '',
      unit: 'unit',
      currentStock: '',
      parLevel: '',
      notes: ''
    });
    setEditingItem(null);
  };

  const resetSupplierForm = () => {
    setSupplierForm({
      name: '',
      phone: '',
      email: '',
      notes: '',
      suppliedItemIds: []
    });
    setEditingSupplier(null);
  };

  const openReorderForItem = (item) => {
    const availableSuppliers = suppliers.filter((supplier) =>
      (supplier.supplied_item_ids || []).includes(item.id)
    );

    const preciseShortfall = toSafeDecimalDifference(item.par_level, item.current_stock, 3);
    const reorderQty = Math.max(preciseShortfall, 1);

    setRequestForm({
      inventoryItemId: item.id,
      supplierId: availableSuppliers.length === 1 ? availableSuppliers[0].id : '',
      requestedQty: formatDecimalInput(reorderQty, 3),
      expectedDate: '',
      note: `Auto reorder from low-stock alert for ${item.name}`
    });

    setShowRequestModal(true);
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!itemForm.name.trim()) {
      await alert('Item name is required', 'Validation', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: itemForm.name.trim(),
        unit: itemForm.unit || 'unit',
        currentStock: itemForm.currentStock === '' ? 0 : Number(itemForm.currentStock),
        parLevel: itemForm.parLevel === '' ? 0 : Number(itemForm.parLevel),
        notes: itemForm.notes || ''
      };

      if (editingItem) {
        await api.put(`/api/inventory/items/${editingItem.id}`, payload);
      } else {
        await api.post('/api/inventory/items', payload);
      }

      setShowItemModal(false);
      resetItemForm();
      await fetchInventoryData();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to save inventory item', 'Error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (item) => {
    const approved = await confirm(`Delete ${item.name}?`, 'Delete Inventory Item', 'warning');
    if (!approved) return;

    try {
      await api.delete(`/api/inventory/items/${item.id}`);
      await fetchInventoryData();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to delete inventory item', 'Error', 'error');
    }
  };

  const handleReceiveStock = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!receiveForm.inventoryItemId || !receiveForm.quantity) {
      await alert('Item and quantity are required', 'Validation', 'warning');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('inventoryItemId', receiveForm.inventoryItemId);
      formData.append('supplierId', receiveForm.supplierId || '');
      formData.append('quantity', String(Number(receiveForm.quantity)));
      formData.append('receivedAt', receiveForm.receivedAt || '');
      formData.append('invoiceNumber', receiveForm.invoiceNumber || '');
      formData.append('invoiceNote', receiveForm.invoiceNote || '');
      formData.append('note', receiveForm.note || '');
      if (stockInvoiceFile) {
        formData.append('invoiceFile', stockInvoiceFile);
      }

      await api.post('/api/inventory/receive', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setReceiveForm({
        inventoryItemId: '',
        supplierId: '',
        quantity: '',
        receivedAt: '',
        invoiceNumber: '',
        invoiceNote: '',
        note: ''
      });
      setStockInvoiceFile(null);

      await fetchInventoryData();
      await alert('Stock received successfully', 'Success', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to receive stock', 'Error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openCreateSupplier = () => {
    resetSupplierForm();
    setShowSupplierModal(true);
  };

  const openEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      notes: supplier.notes || '',
      suppliedItemIds: supplier.supplied_item_ids || []
    });
    setShowSupplierModal(true);
  };

  const handleSaveSupplier = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!supplierForm.name.trim()) {
      await alert('Supplier name is required', 'Validation', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: supplierForm.name.trim(),
        phone: supplierForm.phone || null,
        email: supplierForm.email || null,
        notes: supplierForm.notes || null,
        suppliedItemIds: supplierForm.suppliedItemIds
      };

      if (editingSupplier) {
        await api.put(`/api/inventory/suppliers/${editingSupplier.id}`, payload);
      } else {
        await api.post('/api/inventory/suppliers', payload);
      }

      setShowSupplierModal(false);
      resetSupplierForm();
      await fetchInventoryData();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to save supplier', 'Error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSupplier = async (supplier) => {
    const approved = await confirm(`Delete supplier ${supplier.name}?`, 'Delete Supplier', 'warning');
    if (!approved) return;

    try {
      await api.delete(`/api/inventory/suppliers/${supplier.id}`);
      await fetchInventoryData();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to delete supplier', 'Error', 'error');
    }
  };

  const handleCreatePurchaseRequest = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!requestForm.inventoryItemId || !requestForm.requestedQty) {
      await alert('Item and requested quantity are required', 'Validation', 'warning');
      return;
    }

    const matchedSuppliers = suppliers.filter((supplier) =>
      (supplier.supplied_item_ids || []).includes(requestForm.inventoryItemId)
    );

    if (matchedSuppliers.length > 0 && !requestForm.supplierId) {
      await alert('Please select a registered supplier for this item', 'Supplier Required', 'warning');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/api/inventory/purchase-requests', {
        inventoryItemId: requestForm.inventoryItemId,
        supplierId: requestForm.supplierId || null,
        requestedQty: Number(requestForm.requestedQty),
        expectedDate: requestForm.expectedDate || null,
        note: requestForm.note || null
      });

      const createdRequest = response.data?.request;
      if (createdRequest?.id) {
        try {
          await handleOpenRequestPdfPreview(createdRequest);
        } catch (pdfError) {
          await alert('Request created, but PO preview failed to open. You can open it from the request card.', 'PO Preview', 'warning');
        }
      }

      setShowRequestModal(false);
      setRequestForm({
        inventoryItemId: '',
        supplierId: '',
        requestedQty: '',
        expectedDate: '',
        note: ''
      });
      await fetchInventoryData();
      await alert('Purchase request created. Preview the PO and download when ready.', 'Success', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to create purchase request', 'Error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getPurchaseOrderFileName = (request) => {
    if (request?.id && request?.created_at) {
      return `PO-${String(request.created_at).slice(0, 10).replace(/-/g, '')}-${String(request.id).replace(/-/g, '').slice(0, 8).toUpperCase()}.pdf`;
    }
    return `purchase-order-${request?.id || 'request'}.pdf`;
  };

  const closePoPreviewModal = () => {
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl('');
    setPreviewRequest(null);
    setShowPoPreviewModal(false);
  };

  const handleOpenRequestPdfPreview = async (request) => {
    try {
      const response = await api.get(`/api/inventory/purchase-requests/${request.id}/pdf`, {
        responseType: 'blob'
      });

      if (previewPdfUrl) {
        window.URL.revokeObjectURL(previewPdfUrl);
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setPreviewRequest(request);
      setShowPoPreviewModal(true);
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to open PO preview', 'Error', 'error');
    }
  };

  const handleDownloadRequestPdf = async (request, sourceUrl = '') => {
    try {
      const fileName = getPurchaseOrderFileName(request);
      let url = sourceUrl;

      if (!url) {
        const response = await api.get(`/api/inventory/purchase-requests/${request.id}/pdf?download=true`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        url = window.URL.createObjectURL(blob);
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      if (!sourceUrl) {
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to download PO PDF', 'Error', 'error');
    }
  };

  const handleRequestStatusUpdate = async (request, status) => {
    try {
      await api.patch(`/api/inventory/purchase-requests/${request.id}/status`, { status });
      await fetchInventoryData();
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update request', 'Error', 'error');
    }
  };

  const renderStockTab = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Total Items</div>
            <FiPackage className="text-gray-400 h-4 w-4" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{items.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Low Stock Alerts</div>
            <FiAlertTriangle className="text-red-400 h-4 w-4" />
          </div>
          <div className="text-2xl font-bold text-red-600">{lowStockCount}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Movements</div>
            <FiActivity className="text-gray-400 h-4 w-4" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{movements.length}</div>
        </div>
      </div>

      {lowStockCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
              <FiAlertTriangle className="h-4 w-4" />
              {lowStockCount} low-stock alert{lowStockCount > 1 ? 's' : ''}
            </div>
            <span className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-full font-semibold">Persistent badge</span>
          </div>
          <div className="space-y-2">
            {(alerts.lowStockItems || []).slice(0, 5).map((item) => (
              <div key={item.id} className="bg-white border border-red-100 rounded-lg p-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{item.name}</div>
                  <div className="text-xs text-gray-600">Current: {Number(item.current_stock || 0).toFixed(3)} {item.unit} • Par: {Number(item.par_level || 0).toFixed(3)} {item.unit}</div>
                </div>
                <button
                  onClick={() => openReorderForItem(item)}
                  className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800"
                >
                  Reorder now
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Stock Items</h2>
          <button
            onClick={() => {
              resetItemForm();
              setShowItemModal(true);
            }}
            className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 flex items-center gap-2"
          >
            <FiPlus className="h-3.5 w-3.5" /> Add Item
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Item</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Unit</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Current</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Par</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Status</th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const isLowStock = Number(item.current_stock || 0) <= Number(item.par_level || 0);
              return (
                <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{item.name}</td>
                  <td className="px-4 py-3 text-gray-700">{item.unit}</td>
                  <td className="px-4 py-3 text-gray-700">{Number(item.current_stock || 0).toFixed(3)}</td>
                  <td className="px-4 py-3 text-gray-700">{Number(item.par_level || 0).toFixed(3)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${isLowStock ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {isLowStock ? 'Low Stock' : 'OK'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => openReorderForItem(item)}
                        className="h-9 px-3 text-xs font-semibold border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Reorder
                      </button>
                      <button
                        onClick={() => {
                          setEditingItem(item);
                          setItemForm({
                            name: item.name || '',
                            unit: item.unit || 'unit',
                            currentStock: item.current_stock ?? '',
                            parLevel: item.par_level ?? '',
                            notes: item.notes || ''
                          });
                          setShowItemModal(true);
                        }}
                        className="h-9 w-9 inline-flex items-center justify-center bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        title="Edit"
                      >
                        <FiEdit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item)}
                        className="h-9 w-9 inline-flex items-center justify-center bg-red-50 text-red-700 rounded-lg hover:bg-red-100"
                        title="Delete"
                      >
                        <FiTrash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No inventory items found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderReceiveTab = () => (
    <div className="grid grid-cols-1 gap-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 max-w-4xl">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Stock In Entry (GRN Format)</h2>
        <p className="text-xs text-gray-500 mb-4">Record goods receipt with vendor details, reference document and receiving notes.</p>
        <form onSubmit={handleReceiveStock} className="space-y-3">
          <div className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
            If receipt time is left blank, current time will be used by default (IST).
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Receipt Date & Time</label>
              <input
                type="datetime-local"
                value={receiveForm.receivedAt}
                onChange={(e) => setReceiveForm((prev) => ({ ...prev, receivedAt: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Vendor / Supplier</label>
              <select
                value={receiveForm.supplierId}
                onChange={(e) => setReceiveForm((prev) => ({ ...prev, supplierId: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              >
                <option value="">Select supplier (optional)</option>
                {supplierOptionsForReceiveItem.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Inventory Item</label>
              <select
                value={receiveForm.inventoryItemId}
                onChange={(e) => {
                  setReceiveForm((prev) => ({ ...prev, inventoryItemId: e.target.value, supplierId: '' }));
                }}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                required
              >
                <option value="">Select item</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>{item.name} ({item.unit})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Received Quantity</label>
              <input
                type="number"
                min="0"
                step="0.001"
                value={receiveForm.quantity}
                onChange={(e) => setReceiveForm((prev) => ({ ...prev, quantity: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="e.g. 10.500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Invoice / Challan Number</label>
              <input
                type="text"
                value={receiveForm.invoiceNumber}
                onChange={(e) => setReceiveForm((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="Document reference number"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Document Note</label>
              <input
                type="text"
                value={receiveForm.invoiceNote}
                onChange={(e) => setReceiveForm((prev) => ({ ...prev, invoiceNote: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="Invoice/GRN note"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Invoice File (Optional)</label>
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp"
              onChange={(e) => setStockInvoiceFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
            <p className="text-[11px] text-gray-500 mt-1">Allowed: PDF/JPG/PNG/WEBP up to 10MB</p>
            {stockInvoiceFile && (
              <p className="text-xs text-gray-700 mt-1">Selected: {stockInvoiceFile.name}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Internal Receiving Note</label>
            <textarea
              rows={3}
              value={receiveForm.note}
              onChange={(e) => setReceiveForm((prev) => ({ ...prev, note: e.target.value }))}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
              placeholder="Condition received, quality checks, handler remarks"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-2.5 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Stock In'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Stock In History</h2>
        {stockInReceipts.length === 0 ? (
          <p className="text-sm text-gray-500">No stock-in entries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Received At (IST)</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Item</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Supplier</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Qty</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Invoice</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {stockInReceipts.map((receipt) => (
                  <tr key={receipt.id} className="border-b border-gray-100">
                    <td className="px-3 py-2.5 text-gray-700">{formatISTDateTime(receipt.received_at || receipt.created_at)}</td>
                    <td className="px-3 py-2.5 text-gray-900 font-medium">{receipt.inventory_items?.name || 'Item'}</td>
                    <td className="px-3 py-2.5 text-gray-700">{receipt.inventory_suppliers?.name || '-'}</td>
                    <td className="px-3 py-2.5 text-gray-700">{Number(receipt.received_qty || 0).toFixed(3)} {receipt.inventory_items?.unit || ''}</td>
                    <td className="px-3 py-2.5 text-gray-700">
                      <div className="space-y-0.5">
                        <div>{receipt.invoice_number || '-'}</div>
                        {receipt.invoice_file_url && (
                          <a
                            href={receipt.invoice_file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-semibold text-gray-900 underline hover:text-gray-700"
                          >
                            View file
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex justify-center">
                        <button
                          onClick={() => {
                            setSelectedReceipt(receipt);
                            setShowReceiptDetailsModal(true);
                          }}
                          className="h-8 px-2.5 inline-flex items-center gap-1.5 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          <FiEye className="h-3.5 w-3.5" /> View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderHistoryTab = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Stock Consumption History</h2>
        {consumptionMovements.length === 0 ? (
          <p className="text-sm text-gray-500">No stock consumption entries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">When (IST)</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Item</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Qty Consumed</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Reason</th>
                </tr>
              </thead>
              <tbody>
                {consumptionMovements.map((movement) => (
                  <tr key={movement.id} className="border-b border-gray-100">
                    <td className="px-3 py-2.5 text-gray-700">{formatISTDateTime(movement.created_at)}</td>
                    <td className="px-3 py-2.5 text-gray-900 font-medium">{movement.inventory_items?.name || 'Item'}</td>
                    <td className="px-3 py-2.5 text-red-700 font-semibold">{Math.abs(Number(movement.quantity_change || 0)).toFixed(3)} {movement.inventory_items?.unit || ''}</td>
                    <td className="px-3 py-2.5 text-gray-700">{movement.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {renderEditedStockTab()}
    </div>
  );

  const extractEditedBy = (notes) => {
    const text = String(notes || '');
    const match = text.match(/Manual stock edit by\s(.+?)\s•/);
    return match?.[1] || '-';
  };

  const renderEditedStockTab = () => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <h2 className="text-sm font-semibold text-gray-900 mb-3">Edited Stock History</h2>
      {editedStockHistory.length === 0 ? (
        <p className="text-sm text-gray-500">No manual stock edits yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-y border-gray-200">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">When</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Item</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Change</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Edited By</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Details</th>
              </tr>
            </thead>
            <tbody>
              {editedStockHistory.map((edit) => (
                <tr key={edit.id} className="border-b border-gray-100">
                  <td className="px-3 py-2.5 text-gray-700">{formatISTDateTime(edit.created_at)}</td>
                  <td className="px-3 py-2.5 text-gray-900 font-medium">{edit.inventory_items?.name || 'Item'}</td>
                  <td className={`px-3 py-2.5 font-semibold ${Number(edit.quantity_change || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {Number(edit.quantity_change || 0) > 0 ? '+' : ''}{Number(edit.quantity_change || 0).toFixed(3)} {edit.inventory_items?.unit || ''}
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">{extractEditedBy(edit.notes)}</td>
                  <td className="px-3 py-2.5 text-gray-600 max-w-md">{edit.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderPOTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">PO Registry (Suppliers)</h2>
            <button
              onClick={openCreateSupplier}
              className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 flex items-center gap-2"
            >
              <FiPlus className="h-3.5 w-3.5" /> Add Supplier
            </button>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {suppliers.length === 0 ? (
              <p className="text-sm text-gray-500">No suppliers added yet.</p>
            ) : suppliers.map((supplier) => (
              <div key={supplier.id} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{supplier.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {supplier.phone || '-'} • {supplier.email || '-'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Supplies {(supplier.supplied_item_ids || []).length} item(s)
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditSupplier(supplier)}
                      className="h-8 w-8 inline-flex items-center justify-center bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <FiEdit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteSupplier(supplier)}
                      className="h-8 w-8 inline-flex items-center justify-center bg-red-50 text-red-700 rounded-lg hover:bg-red-100"
                    >
                      <FiTrash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">Purchase Requests</h2>
            <button
              onClick={() => {
                setRequestForm({
                  inventoryItemId: '',
                  supplierId: '',
                  requestedQty: '',
                  expectedDate: '',
                  note: ''
                });
                setShowRequestModal(true);
              }}
              className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 flex items-center gap-2"
            >
              <FiPlus className="h-3.5 w-3.5" /> New Request
            </button>
          </div>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {purchaseRequests.length === 0 ? (
              <p className="text-sm text-gray-500">No purchase requests yet.</p>
            ) : purchaseRequests.map((request) => (
              <div key={request.id} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{request.inventory_items?.name || 'Item'} • {Number(request.requested_qty || 0).toFixed(3)} {request.inventory_items?.unit || ''}</div>
                    <div className="text-xs text-gray-500 mt-0.5">Supplier: {request.inventory_suppliers?.name || 'Not assigned'} • Expected: {request.expected_date || '-'}</div>
                    <div className="text-xs text-gray-500">Created: {new Date(request.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleOpenRequestPdfPreview(request)}
                      className="h-8 px-2.5 inline-flex items-center gap-1.5 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      <FiEye className="h-3.5 w-3.5" /> View PO
                    </button>
                    <select
                      value={request.status}
                      onChange={(e) => handleRequestStatusUpdate(request, e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-lg text-xs bg-white"
                    >
                      <option value="requested">Requested</option>
                      <option value="ordered">Ordered</option>
                      <option value="received">Received</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalyticsTab = () => {
    const projectedRows = analytics.projectedDaysLeft || [];
    const criticalItems = projectedRows.filter((row) => row.projected_days_left !== null && row.projected_days_left <= 3);
    const reorderSoonItems = projectedRows.filter((row) => row.projected_days_left !== null && row.projected_days_left > 3 && row.projected_days_left <= 7);
    const stableItems = projectedRows.filter((row) => row.projected_days_left === null || row.projected_days_left > 7);

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-4">
            <div className="text-xs text-red-600 uppercase tracking-wide font-semibold mb-1">Critical (≤ 3 days)</div>
            <div className="text-2xl font-bold text-red-700">{criticalItems.length}</div>
          </div>
          <div className="bg-white rounded-xl border border-yellow-200 shadow-sm p-4">
            <div className="text-xs text-yellow-700 uppercase tracking-wide font-semibold mb-1">Reorder Soon (4–7 days)</div>
            <div className="text-2xl font-bold text-yellow-700">{reorderSoonItems.length}</div>
          </div>
          <div className="bg-white rounded-xl border border-green-200 shadow-sm p-4">
            <div className="text-xs text-green-700 uppercase tracking-wide font-semibold mb-1">Stable / No Risk</div>
            <div className="text-2xl font-bold text-green-700">{stableItems.length}</div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">Projected Days Left (Priority View)</h2>
            <span className="text-xs text-gray-500">Sorted by urgency</span>
          </div>
          {projectedRows.length === 0 ? (
            <p className="text-sm text-gray-500">Not enough data for projection.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-xs uppercase tracking-wide text-gray-600">Item</th>
                    <th className="text-left py-2 text-xs uppercase tracking-wide text-gray-600">Current</th>
                    <th className="text-left py-2 text-xs uppercase tracking-wide text-gray-600">Avg Daily</th>
                    <th className="text-left py-2 text-xs uppercase tracking-wide text-gray-600">Projected Days</th>
                  </tr>
                </thead>
                <tbody>
                  {projectedRows.map((row) => (
                    <tr key={row.inventory_item_id} className="border-b border-gray-100">
                      <td className="py-2.5 font-medium text-gray-900">{row.name}</td>
                      <td className="py-2.5 text-gray-700">{Number(row.current_stock || 0).toFixed(3)} {row.unit}</td>
                      <td className="py-2.5 text-gray-700">{Number(row.avg_daily_consumption || 0).toFixed(3)} {row.unit}/day</td>
                      <td className="py-2.5">
                        {row.projected_days_left === null ? (
                          <span className="text-gray-400">N/A</span>
                        ) : (
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${row.projected_days_left <= 3 ? 'bg-red-100 text-red-700' : row.projected_days_left <= 7 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                            {row.projected_days_left} days
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Top Consumed Items (30d)</h2>
          {(analytics.topConsumedItems || []).length === 0 ? (
            <p className="text-sm text-gray-500">No consumption data yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {analytics.topConsumedItems.slice(0, 8).map((row) => (
                <div key={row.inventory_item_id} className="flex items-center justify-between border border-gray-100 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-900">{row.name}</div>
                  <div className="text-sm font-semibold text-gray-700">{Number(row.consumed_qty_30d || 0).toFixed(3)} {row.unit}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <Layout><div>Loading inventory...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
            <p className="text-sm text-gray-600 mt-1">Low-stock alerts, PO flow, receiving and usage analytics.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchInventoryData}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium transition-colors"
            >
              <FiRefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={() => {
                resetItemForm();
                setShowItemModal(true);
              }}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm font-medium shadow-sm transition-colors"
            >
              <FiPlus className="h-4 w-4" /> Add Raw Item
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-2">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <button
              onClick={() => setActiveTab(TABS.STOCK)}
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === TABS.STOCK ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
            >
              Stock
            </button>
            <button
              onClick={() => setActiveTab(TABS.STOCK_IN)}
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === TABS.STOCK_IN ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
            >
              Stock In
            </button>
            <button
              onClick={() => setActiveTab(TABS.HISTORY)}
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === TABS.HISTORY ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
            >
              History
            </button>
            <button
              onClick={() => setActiveTab(TABS.PO)}
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeTab === TABS.PO ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
            >
              PO Section
            </button>
            <button
              onClick={() => setActiveTab(TABS.ANALYTICS)}
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 ${activeTab === TABS.ANALYTICS ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
            >
              Analytics
              {lowStockCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === TABS.ANALYTICS ? 'bg-white text-gray-900' : 'bg-red-100 text-red-700'}`}>
                  {lowStockCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {activeTab === TABS.STOCK && renderStockTab()}
        {activeTab === TABS.STOCK_IN && renderReceiveTab()}
        {activeTab === TABS.HISTORY && renderHistoryTab()}
        {activeTab === TABS.PO && renderPOTab()}
        {activeTab === TABS.ANALYTICS && renderAnalyticsTab()}
      </div>

      {showItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setShowItemModal(false); resetItemForm(); }}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">{editingItem ? 'Edit Raw Item' : 'Add Raw Item'}</h3>
            <form onSubmit={handleSaveItem} className="space-y-3">
              <input
                type="text"
                value={itemForm.name}
                onChange={(e) => setItemForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Item name"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                required
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={itemForm.unit}
                  onChange={(e) => setItemForm((prev) => ({ ...prev, unit: e.target.value }))}
                  placeholder="Unit (kg, g, ltr, pcs)"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={itemForm.parLevel}
                  onChange={(e) => setItemForm((prev) => ({ ...prev, parLevel: e.target.value }))}
                  placeholder="Par level"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <input
                type="number"
                step="0.001"
                min="0"
                value={itemForm.currentStock}
                onChange={(e) => setItemForm((prev) => ({ ...prev, currentStock: e.target.value }))}
                placeholder="Current stock"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
              <textarea
                value={itemForm.notes}
                onChange={(e) => setItemForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Notes (optional)"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                rows={3}
              />
              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={saving} className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 disabled:opacity-60">
                  {saving ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
                </button>
                <button type="button" onClick={() => { setShowItemModal(false); resetItemForm(); }} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSupplierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setShowSupplierModal(false); resetSupplierForm(); }}>
          <div className="bg-white rounded-xl p-6 w-full max-w-xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">{editingSupplier ? 'Edit Supplier' : 'Add Supplier'}</h3>
            <form onSubmit={handleSaveSupplier} className="space-y-3">
              <input
                type="text"
                value={supplierForm.name}
                onChange={(e) => setSupplierForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
                placeholder="Supplier name"
                required
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={supplierForm.phone}
                  onChange={(e) => setSupplierForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
                  placeholder="Phone"
                />
                <input
                  type="email"
                  value={supplierForm.email}
                  onChange={(e) => setSupplierForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
                  placeholder="Email"
                />
              </div>
              <textarea
                value={supplierForm.notes}
                onChange={(e) => setSupplierForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg resize-none"
                placeholder="Notes"
                rows={2}
              />

              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm font-semibold text-gray-900 mb-2">Supplied Items</div>
                <div className="max-h-44 overflow-y-auto space-y-1">
                  {items.map((item) => {
                    const checked = supplierForm.suppliedItemIds.includes(item.id);
                    return (
                      <label key={item.id} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setSupplierForm((prev) => ({
                              ...prev,
                              suppliedItemIds: isChecked
                                ? [...prev.suppliedItemIds, item.id]
                                : prev.suppliedItemIds.filter((id) => id !== item.id)
                            }));
                          }}
                        />
                        {item.name} ({item.unit})
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={saving} className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 disabled:opacity-60">
                  {saving ? 'Saving...' : (editingSupplier ? 'Update Supplier' : 'Create Supplier')}
                </button>
                <button type="button" onClick={() => { setShowSupplierModal(false); resetSupplierForm(); }} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowRequestModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Create Purchase Request</h3>
            <form onSubmit={handleCreatePurchaseRequest} className="space-y-3">
              <select
                value={requestForm.inventoryItemId}
                onChange={(e) => setRequestForm((prev) => ({ ...prev, inventoryItemId: e.target.value, supplierId: '' }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
                required
              >
                <option value="">Select item</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>{item.name} ({item.unit})</option>
                ))}
              </select>

              <select
                value={requestForm.supplierId}
                onChange={(e) => setRequestForm((prev) => ({ ...prev, supplierId: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
              >
                <option value="">Select supplier</option>
                {supplierOptionsForRequestItem.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))}
              </select>

              <input
                type="number"
                min="0"
                step="0.001"
                value={requestForm.requestedQty}
                onChange={(e) => setRequestForm((prev) => ({ ...prev, requestedQty: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
                placeholder="Requested quantity"
                required
              />

              <input
                type="date"
                value={requestForm.expectedDate}
                onChange={(e) => setRequestForm((prev) => ({ ...prev, expectedDate: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
              />

              <textarea
                rows={3}
                value={requestForm.note}
                onChange={(e) => setRequestForm((prev) => ({ ...prev, note: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg resize-none"
                placeholder="Note"
              />

              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={saving} className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 disabled:opacity-60">
                  {saving ? 'Creating...' : 'Create Request'}
                </button>
                <button type="button" onClick={() => setShowRequestModal(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReceiptDetailsModal && selectedReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setShowReceiptDetailsModal(false); setSelectedReceipt(null); }}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Stock In Details</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Received At</div>
                <div className="font-medium text-gray-900">{formatISTDateTime(selectedReceipt.received_at || selectedReceipt.created_at)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Supplier</div>
                <div className="font-medium text-gray-900">{selectedReceipt.inventory_suppliers?.name || '-'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Item</div>
                <div className="font-medium text-gray-900">{selectedReceipt.inventory_items?.name || 'Item'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Received Quantity</div>
                <div className="font-medium text-gray-900">{Number(selectedReceipt.received_qty || 0).toFixed(3)} {selectedReceipt.inventory_items?.unit || ''}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Invoice / Challan Number</div>
                <div className="font-medium text-gray-900">{selectedReceipt.invoice_number || '-'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Invoice File</div>
                {selectedReceipt.invoice_file_url ? (
                  <a
                    href={selectedReceipt.invoice_file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-gray-900 underline hover:text-gray-700"
                  >
                    {selectedReceipt.invoice_file_name || 'View invoice file'}
                  </a>
                ) : (
                  <div className="font-medium text-gray-900">-</div>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Purchase Request</div>
                <div className="font-medium text-gray-900">{selectedReceipt.purchase_request_id || '-'}</div>
              </div>
            </div>

            <div className="mt-3 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Document Note</div>
                <div className="text-sm font-medium text-gray-900">{selectedReceipt.invoice_note || '-'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Internal Receiving Note</div>
                <div className="text-sm font-medium text-gray-900">{selectedReceipt.note || '-'}</div>
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <button
                type="button"
                onClick={() => { setShowReceiptDetailsModal(false); setSelectedReceipt(null); }}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showPoPreviewModal && previewRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closePoPreviewModal}>
          <div className="bg-white rounded-xl w-full max-w-5xl h-[85vh] shadow-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Purchase Order Preview</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {previewRequest.inventory_items?.name || 'Item'} • {Number(previewRequest.requested_qty || 0).toFixed(3)} {previewRequest.inventory_items?.unit || ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleDownloadRequestPdf(previewRequest, previewPdfUrl)}
                  className="h-9 px-3 inline-flex items-center gap-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800"
                >
                  <FiDownload className="h-4 w-4" /> Download PO
                </button>
                <button
                  type="button"
                  onClick={closePoPreviewModal}
                  className="h-9 px-3 inline-flex items-center border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100">
              {previewPdfUrl ? (
                <iframe
                  title="Purchase Order Preview"
                  src={previewPdfUrl}
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">Loading PO preview...</div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default InventoryManagement;
